import React, { useReducer, useCallback, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import OverlayContext from "@/components/overlays/OverlayContext";
import { overlayReducer, initialOverlayState, OVERLAY_ACTIONS } from "@/components/overlays/store/overlayReducer";
import OverlayModal from "@/components/overlays/modal/OverlayModal";
import OverlayDrawer from "@/components/overlays/drawer/OverlayDrawer";

/**
 * OverlayProvider — manages a stack of modals and drawers that can coexist
 * independently. Mount once near the root of the React tree (inside Redux
 * Provider) so that overlay content can access all app contexts.
 */
export default function OverlayProvider({ children }) {
  const [state, dispatch] = useReducer(overlayReducer, initialOverlayState);

  // ── Actions exposed via context ────────────────────────────────────

  const openModal = useCallback((key, Component, props, options) => {
    dispatch({ type: OVERLAY_ACTIONS.OPEN_MODAL, payload: { key, Component, props, options } });
  }, []);

  const closeModal = useCallback(key => {
    dispatch({ type: OVERLAY_ACTIONS.START_CLOSE_MODAL, payload: { key } });
  }, []);

  const removeModal = useCallback(key => {
    dispatch({ type: OVERLAY_ACTIONS.REMOVE_MODAL, payload: { key } });
  }, []);

  const closeAllModals = useCallback(() => {
    dispatch({ type: OVERLAY_ACTIONS.CLOSE_ALL_MODALS });
  }, []);

  const openDrawer = useCallback((key, Component, props, options) => {
    dispatch({ type: OVERLAY_ACTIONS.OPEN_DRAWER, payload: { key, Component, props, options } });
  }, []);

  const closeDrawer = useCallback(key => {
    dispatch({ type: OVERLAY_ACTIONS.START_CLOSE_DRAWER, payload: { key } });
  }, []);

  const removeDrawer = useCallback(key => {
    dispatch({ type: OVERLAY_ACTIONS.REMOVE_DRAWER, payload: { key } });
  }, []);

  const closeAllDrawers = useCallback(() => {
    dispatch({ type: OVERLAY_ACTIONS.CLOSE_ALL_DRAWERS });
  }, []);

  const closeAll = useCallback(() => {
    dispatch({ type: OVERLAY_ACTIONS.CLOSE_ALL });
  }, []);

  // ── Combined overlay list sorted by seq (for z-index ordering) ────

  const allOverlays = useMemo(() => {
    const combined = [
      ...state.modals.map(m => ({ ...m, type: "modal" })),
      ...state.drawers.map(d => ({ ...d, type: "drawer" })),
    ];
    return combined.sort((a, b) => a.seq - b.seq);
  }, [state.modals, state.drawers]);

  // The topmost *non-closing* overlay is the one that responds to ESC / backdrop click
  const topmostKey = useMemo(() => {
    const active = allOverlays.filter(o => !o.closing);
    return active.length > 0 ? active[active.length - 1].key : null;
  }, [allOverlays]);

  // ── Global ESC handler (capture phase) ─────────────────────────────

  useEffect(() => {
    if (allOverlays.length === 0) return undefined;

    const handleKeyDown = event => {
      if (event.key !== "Escape") return;

      // Find topmost non-closing overlay
      const active = allOverlays.filter(o => !o.closing);
      if (active.length === 0) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const topmost = active[active.length - 1];
      if (topmost.type === "modal") {
        closeModal(topmost.key);
      } else {
        closeDrawer(topmost.key);
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [allOverlays, closeModal, closeDrawer]);

  // ── Context value ──────────────────────────────────────────────────

  const contextValue = useMemo(
    () => ({
      modals: state.modals,
      drawers: state.drawers,
      openModal,
      closeModal,
      closeAllModals,
      openDrawer,
      closeDrawer,
      closeAllDrawers,
      closeAll,
    }),
    [
      state.modals,
      state.drawers,
      openModal,
      closeModal,
      closeAllModals,
      openDrawer,
      closeDrawer,
      closeAllDrawers,
      closeAll,
    ],
  );

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <OverlayContext.Provider value={contextValue}>
      {children}

      {allOverlays.map((entry, index) => {
        const isTopmost = entry.key === topmostKey;

        if (entry.type === "modal") {
          return (
            <OverlayModal
              key={entry.key}
              entry={entry}
              stackIndex={index}
              isTopmost={isTopmost}
              onClose={closeModal}
              onRemove={removeModal}
            />
          );
        }

        return (
          <OverlayDrawer
            key={entry.key}
            entry={entry}
            stackIndex={index}
            isTopmost={isTopmost}
            onClose={closeDrawer}
            onRemove={removeDrawer}
          />
        );
      })}
    </OverlayContext.Provider>
  );
}

OverlayProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

